import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLayer } from './layer-stack';
import { useLanguage } from './language-context';

// CB-17 sheet: bottom sheet below 768 px, centred dialog above (admin design:
// sheet radius 20 px 20 px 0 0 + sheetUp 200 ms; dialog width min(<w>px, 94vw),
// moRiseC 200 ms, shadow 0 30px 80px, scrim rgba(12,26,36,.4) + blur — VIS §2.4).
// The responsive switch is pure CSS (.sheet in dash.css); the layer stack owns
// Escape, scroll lock and focus return.
export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Desktop dialog width in px (design range 420–880). */
  width?: number;
  guardBack?: boolean;
  /** Extra class on the panel (e.g. `sheet--listbox`). */
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  width = 460,
  guardBack,
  className,
}: SheetProps) {
  const { t } = useLanguage();
  useLayer(open, onClose, { guardBack });
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus into the panel on open so keyboard users land inside the dialog.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    // Body content first (a field, the option list), never the ✕ in the header.
    const body = panel.querySelector<HTMLElement>('.sheet__body');
    const first =
      panel.querySelector<HTMLElement>('[data-autofocus]') ??
      body?.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="listbox"], button:not([disabled])',
      );
    (first ?? panel).focus();
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div className="sheet-layer">
      <div className="scrim" onClick={onClose} aria-hidden="true" data-backdrop />
      <div
        ref={panelRef}
        className={className ? `sheet ${className}` : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        style={{ ['--sheet-w' as string]: `${width}px` }}
      >
        <div className="sheet__grab" aria-hidden="true" />
        <div className="sheet__head">
          <h2 className="sheet__title">{title}</h2>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={t('action.close')}
          >
            ✕
          </button>
        </div>
        <div className="sheet__body">{children}</div>
        {footer ? <div className="sheet__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
